import type { ReactNode } from "react";
import { Box, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import { green, grey, red, yellow } from "@mui/material/colors";
import { Label } from "./Label";
import { emergencyColor } from "./emergency";

const unknown: string = grey[500];
const healthy: string = green[500];
const disconnected: string = red[500];
const warning: string = yellow[500];

// box-shadow and transform both cost no layout space, so the indicator strip
// never shifts when the ring appears - a border/outline would push it around.
const emergencyPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 0 0 3px ${emergencyColor},
      0 0 8px 3px ${alpha(emergencyColor, 0.9)};
  }
  50% {
    transform: scale(1.15);
    box-shadow:
      0 0 0 5px ${alpha(emergencyColor, 0.35)},
      0 0 0 14px ${alpha(emergencyColor, 0)};
  }
`;

export enum Status {
  Healthy,
  Warning,
  Unhealthy,
  Unknown,
}

const StatusIndicator = (props: {
  label?: string;
  status: Status;
  tooltip?: ReactNode;
  emergency?: boolean;
}) => {
  const { label, status, tooltip, emergency } = props;

  const toColor = (status: Status): string => {
    switch (status) {
      case Status.Healthy:
        return healthy;
      case Status.Warning:
        return warning;
      case Status.Unhealthy:
        return disconnected;
      case Status.Unknown:
        return unknown;
    }
  };
  return (
    <>
      <Tooltip title={tooltip}>
        <Box
          sx={{
            backgroundColor: toColor(status),
            height: "25px",
            width: "25px",
            borderRadius: "50%",
            ...(emergency && {
              animation: `${emergencyPulse} 0.85s ease-in-out infinite`,
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
                boxShadow: `0 0 0 3px ${emergencyColor}, 0 0 8px 3px ${alpha(
                  emergencyColor,
                  0.6
                )}`,
              },
            }),
          }}
        />
      </Tooltip>
      {label && <Label emergency={emergency}>{label}</Label>}
    </>
  );
};
export { StatusIndicator };
